import jwt_decode from 'jwt-decode'
import moment from 'moment'

const TOKEN_EVENTS = {
  REFRESH_TOKEN_EXPIRY_NOTICE: 'REFRESH_TOKEN_EXPIRY_NOTICE',
  ACCESS_TOKEN_EXPIRY_NOTICE: 'ACCESS_TOKEN_EXPIRY_NOTICE'
}

const IMPERSONATION_ALLOWED_ROLES = ['ROLE_IMPERSONATION']

const DEFAULT_REFRESH_TTL_SECONDS = 43200 // 12 hours

class TokenService {
  static _refreshTokenExpiryIntervalId = null
  static _accessTokenRenewalIntervalId = null

  constructor(accessToken, refreshToken) {
    if (!accessToken) throw new Error('Missing access token.')
    if (!refreshToken) throw new Error('Missing refresh token.')

    this.clearRefreshTokenExpiryAlert()
    this.clearAccessTokenRenewal()

    this.accessToken = accessToken
    this.refreshToken = refreshToken
    this.tokenDetails = getTokenDetails(this.accessToken, this.refreshToken)
    saveToLocalStorage(this.tokenDetails)

    this.clearAccessTokenRenewal()
    this.triggerAccessTokenRenewal()
  }

  clearRefreshTokenExpiryAlert() {
    if (TokenService._refreshTokenExpiryIntervalId) {
      clearInterval(TokenService._refreshTokenExpiryIntervalId)
      TokenService._refreshTokenExpiryIntervalId = null
    }
  }

  clearAccessTokenRenewal() {
    if (TokenService._accessTokenRenewalIntervalId) {
      clearInterval(TokenService._accessTokenRenewalIntervalId)
      TokenService._accessTokenRenewalIntervalId = null
    }
  }

  setupRefreshTokenExpiryAlert(alertInSecondsBeforeExpiry = 1800, checkEveryNSeconds = 60) {
    let tokenDetails = loadFromLocalStorage()
    if (!tokenDetails) return

    this.clearRefreshTokenExpiryAlert()

    TokenService._refreshTokenExpiryIntervalId = setInterval(() => {
      const currentUnixEpochTime = moment().unix()
      const maxExpiryFromAccessOrRefreshToken = Math.max(
        tokenDetails.refreshTokenExpiry,
        tokenDetails.accessTokenExpiry
      )
      const timeInSecondsToExpiry = maxExpiryFromAccessOrRefreshToken - currentUnixEpochTime
      if (timeInSecondsToExpiry > alertInSecondsBeforeExpiry) return

      clearInterval(TokenService._refreshTokenExpiryIntervalId)
      TokenService._refreshTokenExpiryIntervalId = null
      sendEventToParent(TOKEN_EVENTS.REFRESH_TOKEN_EXPIRY_NOTICE)
    }, checkEveryNSeconds * 1000)
  }

  triggerAccessTokenRenewal(secondsBeforeExpiry = 300, checkEveryNSeconds = 120) {
    this.clearAccessTokenRenewal()

    TokenService._accessTokenRenewalIntervalId = setInterval(() => {
      const tokenDetails = loadFromLocalStorage()
      if (!tokenDetails) {
        this.clearAccessTokenRenewal()
        return
      }

      const currentUnixEpochTime = moment().unix()
      const timeInSecondsToExpiry = tokenDetails.accessTokenExpiry - currentUnixEpochTime
      const isRefreshTokenExpired = tokenDetails.refreshTokenExpiry <= currentUnixEpochTime

      if (timeInSecondsToExpiry > secondsBeforeExpiry) return

      // Send event before clearing the interval
      sendEventToParent(TOKEN_EVENTS.ACCESS_TOKEN_EXPIRY_NOTICE)

      if (isRefreshTokenExpired) {
        this.clearAccessTokenRenewal()
      }
    }, checkEveryNSeconds * 1000)
  }

  static updateTokenDetails(accessToken, refreshToken) {
    const tokenDetails = getTokenDetails(accessToken, refreshToken)
    saveToLocalStorage(tokenDetails)
  }

  static getTokenDetails() {
    return loadFromLocalStorage()
  }

  static clearTokenDetails() {
    removeTokenDetailsFromLocalStorage()
  }

  static canImpersonate() {
    const { accessToken, refreshToken } = TokenService.getTokenDetails()
    const { roles } = getTokenDetails(accessToken, refreshToken)
    return roles?.filter((role) => IMPERSONATION_ALLOWED_ROLES.includes(role))?.length > 0
  }
}

function sendEventToParent(eventType) {
  const event = new Event(eventType)
  dispatchEvent(event)
}

function removeTokenDetailsFromLocalStorage() {
  localStorage.removeItem('tokenDetails')
}

function getTokenDetails(accessToken, refreshToken) {
  const accessTokenDetails = jwt_decode(accessToken)

  let storedTokenDetails = localStorage.getItem('tokenDetails')
  if (storedTokenDetails) {
    try {
      storedTokenDetails = JSON.parse(storedTokenDetails)
    } catch {
      storedTokenDetails = null
    }
  }

  let refreshTokenExpiry

  if (storedTokenDetails && storedTokenDetails.refreshToken === refreshToken && storedTokenDetails.refreshTokenExpiry) {
    refreshTokenExpiry = storedTokenDetails.refreshTokenExpiry
  } else {
    refreshTokenExpiry = moment().unix() + DEFAULT_REFRESH_TTL_SECONDS
  }

  return {
    userId: accessTokenDetails?.user_id,
    userEmail: accessTokenDetails?.user_name,
    userName: accessTokenDetails?.user_name,
    roles: accessTokenDetails?.authorities,
    accessToken,
    refreshToken,
    accessTokenExpiry: accessTokenDetails?.exp,
    refreshTokenExpiry
  }
}

function loadFromLocalStorage() {
  const tokenDetails = localStorage.getItem('tokenDetails')
  if (!tokenDetails) return
  return JSON.parse(tokenDetails)
}

function saveToLocalStorage(tokenDetails) {
  localStorage.setItem('tokenDetails', JSON.stringify(tokenDetails))
}

export { TokenService }
